<template>
  <!-- Header: Replaced IDs and template classes with Tailwind -->
  <header id="header" class="bg-white shadow-sm sticky top-0 z-40">
    <div id="header-wrap" class="border-b border-gray-200">
      <!-- Container: Replaced template container/clearfix with Tailwind container and flex -->
      <!-- Reordered Logo and Mobile Menu Trigger for mobile view -->
      <div class="container mx-auto px-4 flex items-center justify-between h-16">
        <!-- Logo -->
        <div id="logo" class="flex-shrink-0">
          <!-- Need to handle locale prefix dynamically -->
          <!-- Removed standard-logo, assuming retina handles responsiveness -->
          <Link href="/" class="block">
            <img src="https://juicysms.com/cdn-cgi/image/height=200,format=auto,quality=95/images/logo@2x.png" alt="JuicySMS Logo" class="h-25 w-auto" width="250" height="200" fetchpriority="high">
          </Link>
        </div><!-- #logo end -->

        <!-- Primary Navigation (Desktop) -->
        <nav id="primary-menu" class="hidden lg:flex lg:items-center lg:ml-auto">
          <div class="flex items-center space-x-1">
             <!-- Static Links - Updated active class styling to use brand colors -->
             <Link :href="welcomeRoute" :class="[
                'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                ['welcome', 'welcome.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_home') }}
             </Link>
             <Link :href="hireRoute" :class="[
                 'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                 ['hire', 'hire.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_rent_number') }}
             </Link>
             <Link :href="hdiwRoute" :class="[
                 'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                 ['hdiw', 'hdiw.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_hdiw') }}
             </Link>
             <Link :href="faqRoute" :class="[
                 'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                 ['faq', 'faq.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_faq') }}
             </Link>
             <Link :href="supportRoute" :class="[
                 'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                 ['support', 'support.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_help') }}
             </Link>
             <Link :href="route('api')" :class="[
                 'px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out',
                 ziggy.name === 'api' ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
             ]">
                {{ $t('global.nav_api') }}
             </Link>
             <!-- Logged-in only: guests (incl. AdsBot and the guest-only edge cache) must never see this outbound proxy link — Google Ads flagged it (Aug 2026, 38a47d94b). -->
             <a v-if="auth.user" class="text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out" target="_blank" rel="noreferrer" href="https://juicyproxies.com">
                Mobile Proxies <span class="text-red-600 text-xs font-bold align-super">NEW</span>
             </a>
             <!-- Brand Colors Link Removed -->

             <!-- Language Switcher Dropdown -->
             <div class="relative" ref="langDropdownRef" @mouseenter="handleMouseEnter('lang')" @mouseleave="handleMouseLeave('lang')"> <!-- Removed group, Added mouse events -->
                <button @click="toggleDropdown('lang')" type="button" class="text-gray-700 bg-white rounded-md inline-flex items-center text-sm font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 hover:bg-gray-50 transition duration-150 ease-in-out"> <!-- Removed group-hover -->
                    <span :class="`fi fi-${currentLanguage.flag} rounded mr-2`"></span>
                    <span>{{ currentLanguage.code.toUpperCase() }}</span>
                    <svg class="ml-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"> <!-- Removed group-hover -->
                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                     </svg>
                 </button>
                 <div :class="{
                     'absolute z-50 left-0 pt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-out duration-200': true,
                     'opacity-100 visible': isLangDropdownOpen || activeHoverType === 'lang',
                     'opacity-0 invisible': !isLangDropdownOpen && activeHoverType !== 'lang'
                 }">
                     <div class="py-1" role="none">
                         <Link v-for="lang in availableLanguages" :key="lang.code" :href="languageHref(lang.code)" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"> <!-- Use closeAllDropdowns -->
                            <div class="flex items-center">
                                <span :class="`fi fi-${lang.flag} rounded mr-2`"></span>
                                {{ lang.name }}
                            </div>
                        </Link>
                    </div>
                </div>
             </div>

             <!-- Currency Switcher Dropdown -->
             <div class="relative" ref="currencyDropdownRef" @mouseenter="handleMouseEnter('currency')" @mouseleave="handleMouseLeave('currency')"> <!-- Removed group, Added mouse events -->
                 <button @click="toggleDropdown('currency')" type="button" class="text-gray-700 bg-white rounded-md inline-flex items-center text-sm font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 hover:bg-gray-50 transition duration-150 ease-in-out"> <!-- Removed group-hover -->
                    <font-awesome-icon :icon="currencyIcon" class="mr-2" /><span>{{ currency.code }}</span>
                    <svg class="ml-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"> <!-- Removed group-hover -->
                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                     </svg>
                  </button>
                   <div :class="{
                      'absolute z-50 left-0 pt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-out duration-200': true,
                      'opacity-100 visible': isCurrencyDropdownOpen || activeHoverType === 'currency',
                      'opacity-0 invisible': !isCurrencyDropdownOpen && activeHoverType !== 'currency'
                  }">
                     <div class="py-1" role="none">
                          <Link v-if="currency.code !== 'EUR'" :href="route('currency.change', 'EUR')" @click="closeAllDropdowns" preserve-scroll class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"> <!-- Use closeAllDropdowns -->
                             <div class="flex items-center">
                                <font-awesome-icon :icon="faEuroSign" class="mr-2 w-4 text-center" />Euro (EUR)
                             </div>
                         </Link>
                         <Link v-if="currency.code !== 'USD'" :href="route('currency.change', 'USD')" @click="closeAllDropdowns" preserve-scroll class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"> <!-- Use closeAllDropdowns -->
                             <div class="flex items-center">
                                <font-awesome-icon :icon="faDollarSign" class="mr-2 w-4 text-center" />US Dollar (USD)
                             </div>
                         </Link>
                         <Link v-if="currency.code !== 'GBP'" :href="route('currency.change', 'GBP')" @click="closeAllDropdowns" preserve-scroll class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"> <!-- Use closeAllDropdowns -->
                             <div class="flex items-center">
                                <font-awesome-icon :icon="faSterlingSign" class="mr-2 w-4 text-center" />British Pound (GBP)
                            </div>
                         </Link>
                    </div>
                 </div>
             </div>

             <!-- Authenticated: User Dropdown -->
             <div v-if="auth.user" class="relative ml-4" ref="userDropdownRef" @mouseenter="handleMouseEnter('user')" @mouseleave="handleMouseLeave('user')">
                 <button @click="toggleDropdown('user')" type="button" aria-label="User menu" class="text-gray-700 bg-white rounded-md inline-flex items-center text-sm font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-3 py-2 hover:bg-gray-50 transition duration-150 ease-in-out">
                    <font-awesome-icon :icon="faUser" class="text-lg" />
                    <svg class="ml-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                     </svg>
                  </button>
                  <div :class="{
                      'absolute z-50 right-0 pt-2 w-48 origin-top-right rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-out duration-200': true,
                      'opacity-100 visible': isUserDropdownOpen || activeHoverType === 'user',
                      'opacity-0 invisible': !isUserDropdownOpen && activeHoverType !== 'user'
                  }">
                     <div class="py-1" role="none">
                          <Link :href="route('myaccount')" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"><font-awesome-icon :icon="faUser" class="mr-2 w-4 text-center" />{{ $t('global.nav_myacc') }}</Link>
                         <Link :href="route('addfunds')" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"><font-awesome-icon :icon="faMoneyBill" class="mr-2 w-4 text-center" />{{ $t('global.nav_addfunds') }}</Link>
                         <Link :href="route('myorders')" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"><font-awesome-icon :icon="faHistory" class="mr-2 w-4 text-center" />{{ $t('global.nav_myorders') }}</Link>
                         <Link v-if="auth.user && auth.user.hasActiveHireContracts" :href="route('hire.index')" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"><font-awesome-icon :icon="faGaugeHigh" class="mr-2 w-4 text-center" />{{ $t('global.nav_myhireorders') }}</Link>
                         <Link :href="route('referrals')" @click="closeAllDropdowns" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem"><font-awesome-icon :icon="faUsers" class="mr-2 w-4 text-center" />{{ $t('global.nav_referrals') }}</Link>
                         <Link :href="route('logout')" @click="closeAllDropdowns" method="post" as="button" type="button" class="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition duration-150 ease-in-out" role="menuitem">
                             <font-awesome-icon :icon="faRightFromBracket" class="mr-2 w-4 text-center" />{{ $t('global.nav_logout') }}
                         </Link>
                    </div>
                  </div>
             </div>

             <!-- Guest: explicit Login + Register buttons (visible, not buried in a dropdown) -->
             <div v-else class="flex items-center ml-4 space-x-2">
                 <Link :href="route('login')" class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out">
                     <font-awesome-icon :icon="faRightToBracket" class="mr-2" />{{ $t('global.nav_login') }}
                 </Link>
                 <Link :href="route('register')" class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-transparent text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out">
                     <font-awesome-icon :icon="faUserPlus" class="mr-2" />{{ $t('global.nav_register') }}
                 </Link>
             </div>
          </div>
        </nav><!-- #primary-menu end -->

        <!-- Mobile Menu Trigger (Moved to the right for mobile) -->
        <div id="primary-menu-trigger" @click="toggleMobileMenu" class="lg:hidden cursor-pointer p-2 -ml-2"> <!-- Adjusted margin -->
          <font-awesome-icon :icon="isMobileMenuOpen ? faTimes : faBars" class="text-xl" />
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <nav v-show="isMobileMenuOpen" class="lg:hidden absolute top-full left-0 right-0 bg-white shadow-md border-t border-gray-200 z-30 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <!-- Mobile Links: Updated active class styling to use brand colors -->
          <Link :href="welcomeRoute" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ['welcome', 'welcome.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_home') }}</Link>
          <Link :href="hireRoute" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ['hire', 'hire.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_rent_number') }}</Link>
          <Link :href="hdiwRoute" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ['hdiw', 'hdiw.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_hdiw') }}</Link>
          <Link :href="faqRoute" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ['faq', 'faq.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_faq') }}</Link>
          <Link :href="supportRoute" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ['support', 'support.localized'].includes(ziggy.name) ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_help') }}</Link>
          <Link :href="route('api')" @click="closeMobileMenu" :class="[
              'block px-3 py-2 rounded-md text-base font-medium',
              ziggy.name === 'api' ? 'bg-brand-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          ]">{{ $t('global.nav_api') }}</Link>
          <!-- Logged-in only, see the desktop nav above. -->
          <a v-if="auth.user" @click="closeMobileMenu" class="text-gray-700 hover:bg-gray-100 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium" target="_blank" rel="noreferrer" href="https://juicyproxies.com">Mobile Proxies <span class="text-red-600 text-xs font-bold align-super">NEW</span></a>
          <!-- Brand Colors Link Removed -->

          <!-- Mobile User/Auth Links -->
          <div class="border-t border-gray-200 pt-4 pb-3">
            <div v-if="auth.user" class="space-y-1">
              <Link :href="route('myaccount')" @click="closeMobileMenu" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"><font-awesome-icon :icon="faUser" class="mr-2 w-4 text-center" />{{ $t('global.nav_myacc') }}</Link>
              <Link :href="route('addfunds')" @click="closeMobileMenu" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"><font-awesome-icon :icon="faMoneyBill" class="mr-2 w-4 text-center" />{{ $t('global.nav_addfunds') }}</Link>
              <Link :href="route('myorders')" @click="closeMobileMenu" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"><font-awesome-icon :icon="faHistory" class="mr-2 w-4 text-center" />{{ $t('global.nav_myorders') }}</Link>
              <Link v-if="auth.user && auth.user.hasActiveHireContracts" :href="route('hire.index')" @click="closeMobileMenu" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"><font-awesome-icon :icon="faGaugeHigh" class="mr-2 w-4 text-center" />{{ $t('global.nav_myhireorders') }}</Link>
              <Link :href="route('referrals')" @click="closeMobileMenu" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"><font-awesome-icon :icon="faUsers" class="mr-2 w-4 text-center" />{{ $t('global.nav_referrals') }}</Link>
              <Link :href="route('logout')" @click="closeMobileMenu" method="post" as="button" type="button" class="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                <font-awesome-icon :icon="faRightFromBracket" class="mr-2 w-4 text-center" />{{ $t('global.nav_logout') }}
              </Link>
            </div>
            <div v-else class="px-3 grid grid-cols-2 gap-2">
              <Link :href="route('login')" @click="closeMobileMenu" class="inline-flex justify-center items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
                <font-awesome-icon :icon="faRightToBracket" class="mr-2" />{{ $t('global.nav_login') }}
              </Link>
              <Link :href="route('register')" @click="closeMobileMenu" class="inline-flex justify-center items-center px-3 py-2 rounded-md text-sm font-medium border border-transparent text-white bg-brand-primary hover:bg-brand-primary-dark">
                <font-awesome-icon :icon="faUserPlus" class="mr-2" />{{ $t('global.nav_register') }}
              </Link>
            </div>
          </div>

          <!-- Mobile Language/Currency Switchers (Simplified) -->
           <!-- Mobile Language Switcher (Collapsible) -->
           <div class="border-t border-gray-200 pt-3">
                <div @click="toggleMobileLangDropdown" class="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-md">
                     <span class="flex items-center text-sm font-medium text-gray-700"> <!-- Display current language -->
                         <span :class="`fi fi-${currentLanguage.flag} rounded mr-2`"></span>
                         {{ currentLanguage.code.toUpperCase() }}
                    </span>
                     <font-awesome-icon :icon="isMobileLangOpen ? faChevronUp : faChevronDown" class="text-gray-400 text-xs" />
                </div>
                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <div v-show="isMobileLangOpen" class="mt-1 space-y-1 pl-3 max-h-48 overflow-y-auto"> <!-- Added max-h and overflow -->
                        <Link v-for="lang in availableLanguages" :key="lang.code" :href="languageHref(lang.code)" @click="closeMobileMenu" class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                            <span :class="`fi fi-${lang.flag} rounded mr-2`"></span> {{ lang.name }}
                        </Link>
                    </div>
                </transition>
           </div>
           <!-- Mobile Currency Switcher (Collapsible) -->
           <div class="border-t border-gray-200 pt-3 pb-3">
                <div @click="toggleMobileCurrencyDropdown" class="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-md">
                     <span class="flex items-center text-sm font-medium text-gray-700"> <!-- Display current currency -->
                        <font-awesome-icon :icon="currencyIcon" class="mr-2 w-4 text-center" />{{ currency.code }}
                     </span>
                     <font-awesome-icon :icon="isMobileCurrencyOpen ? faChevronUp : faChevronDown" class="text-gray-400 text-xs" />
                </div>
                 <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <div v-show="isMobileCurrencyOpen" class="mt-1 space-y-1 pl-3">
                        <Link v-if="currency.code !== 'EUR'" :href="route('currency.change', 'EUR')" @click="closeMobileMenu" preserve-scroll class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                            <font-awesome-icon :icon="faEuroSign" class="mr-2 w-4 text-center" /> Euro (EUR)
                        </Link>
                        <Link v-if="currency.code !== 'USD'" :href="route('currency.change', 'USD')" @click="closeMobileMenu" preserve-scroll class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                            <font-awesome-icon :icon="faDollarSign" class="mr-2 w-4 text-center" /> US Dollar (USD)
                        </Link>
                        <Link v-if="currency.code !== 'GBP'" :href="route('currency.change', 'GBP')" @click="closeMobileMenu" preserve-scroll class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                            <font-awesome-icon :icon="faSterlingSign" class="mr-2 w-4 text-center" /> British Pound (GBP)
                         </Link>
                    </div>
                 </transition>
                <!-- Removed extra closing tags -->
           </div>

        </div>
      </nav>
    </transition>

  </header><!-- #header end -->
</template>

<script setup>
import { Link, usePage } from '@inertiajs/vue3';
import { computed, ref, onMounted, onUnmounted } from 'vue'; // Import onMounted, onUnmounted
import { faEuroSign, faDollarSign, faSterlingSign, faUser, faMoneyBill, faHistory, faGaugeHigh, faUsers, faRightFromBracket, faWandMagicSparkles, faLink, faTimes, faBars, faChevronUp, faChevronDown, faRightToBracket, faUserPlus } from '@fortawesome/free-solid-svg-icons'; // Import FA icons

// Mobile menu state
const isMobileMenuOpen = ref(false);
const isMobileLangOpen = ref(false); // State for mobile language dropdown
const isMobileCurrencyOpen = ref(false); // State for mobile currency dropdown

// Desktop dropdown states
const isLangDropdownOpen = ref(false);
const isCurrencyDropdownOpen = ref(false);
const isUserDropdownOpen = ref(false);
const activeHoverType = ref(null); // Track active hover type
const hoverEnterTimeoutId = ref(null); // Timeout ID for enter delay
const hoverLeaveTimeoutId = ref(null); // Timeout ID for leave delay

// Refs for dropdown containers
const langDropdownRef = ref(null);
const currencyDropdownRef = ref(null);
const userDropdownRef = ref(null);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  // Close dropdowns when main menu is toggled
  if (isMobileMenuOpen.value) {
    isMobileLangOpen.value = false;
    isMobileCurrencyOpen.value = false;
  }
  // Close desktop dropdowns if mobile menu opens
  closeAllDropdowns();
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
  isMobileLangOpen.value = false; // Also close dropdowns
  isMobileCurrencyOpen.value = false;
};

const toggleMobileLangDropdown = () => {
    isMobileLangOpen.value = !isMobileLangOpen.value;
    if (isMobileLangOpen.value) isMobileCurrencyOpen.value = false; // Close other dropdown
};

const toggleMobileCurrencyDropdown = () => {
    isMobileCurrencyOpen.value = !isMobileCurrencyOpen.value;
      if (isMobileCurrencyOpen.value) isMobileLangOpen.value = false; // Close other dropdown
 };

// Mouse Enter/Leave Handlers with Delay
const handleMouseEnter = (type) => {
    clearTimeout(hoverLeaveTimeoutId.value); // Clear any pending leave timeout
    clearTimeout(hoverEnterTimeoutId.value); // Clear any pending enter timeout
    hoverEnterTimeoutId.value = setTimeout(() => {
        // Only activate hover if no dropdown is currently clicked open
        if (!isLangDropdownOpen.value && !isCurrencyDropdownOpen.value && !isUserDropdownOpen.value) {
            activeHoverType.value = type;
        }
    }, 150); // 150ms delay before showing on hover
};

const handleMouseLeave = (type) => {
    clearTimeout(hoverEnterTimeoutId.value); // Clear any pending enter timeout
    clearTimeout(hoverLeaveTimeoutId.value); // Clear any pending leave timeout
    hoverLeaveTimeoutId.value = setTimeout(() => {
        // Only deactivate hover if this type was the one actively hovered
        if (activeHoverType.value === type) {
            activeHoverType.value = null;
        }
    }, 150); // 150ms delay before hiding on leave
};

// Function to close all dropdowns (click and hover states)
const closeAllDropdowns = () => {
    clearTimeout(hoverEnterTimeoutId.value);
    clearTimeout(hoverLeaveTimeoutId.value);
    activeHoverType.value = null;
    isLangDropdownOpen.value = false;
    isCurrencyDropdownOpen.value = false;
    isUserDropdownOpen.value = false;
};

// Modified toggle function
const toggleDropdown = (type) => {
    clearTimeout(hoverEnterTimeoutId.value); // Clear timeouts
    clearTimeout(hoverLeaveTimeoutId.value);
    activeHoverType.value = null; // Clear hover state

    let targetStateRef;
    let otherOpenStateRefs = [];

    if (type === 'lang') {
        targetStateRef = isLangDropdownOpen;
        otherOpenStateRefs = [isCurrencyDropdownOpen, isUserDropdownOpen];
    } else if (type === 'currency') {
        targetStateRef = isCurrencyDropdownOpen;
        otherOpenStateRefs = [isLangDropdownOpen, isUserDropdownOpen];
    } else if (type === 'user') {
        targetStateRef = isUserDropdownOpen;
        otherOpenStateRefs = [isLangDropdownOpen, isCurrencyDropdownOpen];
    } else {
        return; // Should not happen
    }

    const wasOpen = targetStateRef.value;

    // Close other open states first
    otherOpenStateRefs.forEach(ref => ref.value = false);

    // Toggle the target open state
    targetStateRef.value = !wasOpen;
};


// Click outside handler
const handleClickOutside = (event) => {
    // Check if the click is outside all dropdown containers
    const clickedOutsideLang = langDropdownRef.value && !langDropdownRef.value.contains(event.target);
    const clickedOutsideCurrency = currencyDropdownRef.value && !currencyDropdownRef.value.contains(event.target);
    const clickedOutsideUser = userDropdownRef.value && !userDropdownRef.value.contains(event.target);

    // Also check if the click target is the mobile menu trigger, close desktop dropdowns if so
    const mobileTrigger = document.getElementById('primary-menu-trigger');
    const clickedMobileTrigger = mobileTrigger && mobileTrigger.contains(event.target);

    if (clickedOutsideLang && clickedOutsideCurrency && clickedOutsideUser && !clickedMobileTrigger) {
        closeAllDropdowns();
    }
};

// Add/remove listener
onMounted(() => {
    document.addEventListener('click', handleClickOutside, true); // Use capture phase
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true);
});


 // Access shared data
const page = usePage();
const auth = computed(() => page.props.auth);
const locale = computed(() => page.props.locale || 'en'); // Add default for locale too
const translations = computed(() => page.props.translations || {}); // Add default for translations
const ziggy = computed(() => page.props.ziggy || { name: null }); // Default ziggy object

// Define currency with defaults and icon mapping
const currency = computed(() => page.props.currency || { code: 'EUR', symbol: '€', rates: {} });

// Computed property for the currency icon
const currencyIcon = computed(() => {
    switch (currency.value.code) {
        case 'USD': return faDollarSign;
        case 'GBP': return faSterlingSign;
        case 'EUR':
        default: return faEuroSign;
    }
});


// Define available languages based on Blade logic
const availableLanguages = computed(() => {
    const languages = [
        { code: 'en', name: 'English', flag: currency.value.code === 'USD' ? 'us' : 'gb' },
        { code: 'zh', name: 'Chinese - 中文', flag: 'cn' },
        { code: 'ru', name: 'Russian - русский', flag: 'ru' },
        { code: 'nl', name: 'Dutch - Nederlands', flag: 'nl' },
        { code: 'es', name: 'Spanish - Español', flag: 'es' },
        { code: 'fr', name: 'French - Français', flag: 'fr' },
        { code: 'de', name: 'German - Deutsch', flag: 'de' },
        { code: 'pl', name: 'Polish - Polski', flag: 'pl' },
        { code: 'ar', name: 'Arabic - العربية', flag: 'sa' },
        { code: 'pt', name: 'Portuguese - Português', flag: 'br' },
        { code: 'tr', name: 'Turkish - Türkçe', flag: 'tr' },
        { code: 'ja', name: 'Japanese - 日本語', flag: 'jp' },
        { code: 'ko', name: 'Korean - 한국어', flag: 'kr' },
        { code: 'it', name: 'Italian - Italiano', flag: 'it' },
        { code: 'id', name: 'Indonesian - Bahasa Indonesia', flag: 'id' },
        { code: 'hi', name: 'Hindi - हिन्दी', flag: 'in' },
        { code: 'vi', name: 'Vietnamese - Tiếng Việt', flag: 'vn' },
        { code: 'th', name: 'Thai - ไทย', flag: 'th' },
        { code: 'uk', name: 'Ukrainian - Українська', flag: 'ua' },
    ];
    // Filter out the current locale
    return languages.filter(lang => lang.code !== locale.value);
});

// The pages that exist in every language, as LanguageController lists them. For those the
// switcher links straight to the translation (/zh/faq) instead of /lang/zh, which only
// redirects there: one request fewer, and a plain link a crawler can follow. Baidu ignores
// hreflang, so links are how it finds /zh. English keeps /lang/en, because that is what
// clears a saved language: with "zh" saved, /faq redirects to /zh.
const LOCALIZED_PAGES = ['', 'faq', 'support', 'hdiw', 'hire', 'blog'];

const languageHref = (code) => {
    if (code === 'en') return '/lang/en';
    const codes = [locale.value, ...availableLanguages.value.map(lang => lang.code)];
    const segments = (page.url || '/').split(/[?#]/)[0].split('/').filter(Boolean);
    if (segments.length && codes.includes(segments[0])) segments.shift();
    if (!LOCALIZED_PAGES.includes(segments[0] ?? '')) return `/lang/${code}`;
    return '/' + [code, ...segments].join('/');
};

const currentLanguage = computed(() => {
     const languages = [
        { code: 'en', name: 'English', flag: currency.value.code === 'USD' ? 'us' : 'gb' },
        { code: 'zh', name: 'Chinese - 中文', flag: 'cn' },
        { code: 'ru', name: 'Russian - русский', flag: 'ru' },
        { code: 'nl', name: 'Dutch - Nederlands', flag: 'nl' },
        { code: 'es', name: 'Spanish - Español', flag: 'es' },
        { code: 'fr', name: 'French - Français', flag: 'fr' },
        { code: 'de', name: 'German - Deutsch', flag: 'de' },
        { code: 'pl', name: 'Polish - Polski', flag: 'pl' },
        { code: 'ar', name: 'Arabic - العربية', flag: 'sa' },
        { code: 'pt', name: 'Portuguese - Português', flag: 'br' },
        { code: 'tr', name: 'Turkish - Türkçe', flag: 'tr' },
        { code: 'ja', name: 'Japanese - 日本語', flag: 'jp' },
        { code: 'ko', name: 'Korean - 한국어', flag: 'kr' },
        { code: 'it', name: 'Italian - Italiano', flag: 'it' },
        { code: 'id', name: 'Indonesian - Bahasa Indonesia', flag: 'id' },
        { code: 'hi', name: 'Hindi - हिन्दी', flag: 'in' },
        { code: 'vi', name: 'Vietnamese - Tiếng Việt', flag: 'vn' },
        { code: 'th', name: 'Thai - ไทย', flag: 'th' },
        { code: 'uk', name: 'Ukrainian - Українська', flag: 'ua' },
    ];
    return languages.find(lang => lang.code === locale.value) || languages[0]; // Default to English if not found
});

// Translation helper using shared translations
const __ = (key) => {
    const parts = key.split('.');
    let translation = translations.value;
    try {
        parts.forEach(part => {
            translation = translation[part];
        });
        return translation || key; // Return key if translation not found
    } catch (e) {
        return key; // Return key on error
    }
};

// Computed properties for localized routes
const welcomeRoute = computed(() => {
  return locale.value === 'en'
    ? route('welcome')
    : route('welcome.localized', { locale: locale.value });
});

const hireRoute = computed(() => {
  // Correct route names based on web.php (reverted)
  return locale.value === 'en'
    ? route('hire')
    : route('hire.localized', { locale: locale.value });
});

const hdiwRoute = computed(() => {
  return locale.value === 'en'
    ? route('hdiw')
    : route('hdiw.localized', { locale: locale.value });
});

const faqRoute = computed(() => {
  return locale.value === 'en'
    ? route('faq')
    : route('faq.localized', { locale: locale.value });
});

const supportRoute = computed(() => {
  return locale.value === 'en'
    ? route('support')
    : route('support.localized', { locale: locale.value });
});


// TODO: Implement logic for showing hire panel link based on user data (e.g., add 'hasActiveHireContracts' to shared user data)
// TODO: Implement logic for showing hire panel link based on user data (e.g., add 'hasActiveHireContracts' to shared user data)
</script>
